import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Smartphone, PenTool, BarChart3, Check, Code, Database, Server, ShoppingCart, Layers, Zap, Figma, Layout, FileText, PieChart, Brain, Cloud, Users } from 'lucide-react';
import Hero from '../components/Hero';
import CtaSection from '../components/CtaSection';
import TestimonialsSection from '../components/TestimonialsSection';

const ServicesPage: React.FC = () => {
  return (
    <>
      <Hero
        title="We turn ideas into high-performance digital products"
        subtitle="Web apps, mobile solutions, and data platforms—built with modern tech and agile methods."
        buttonText="Get Started"
        buttonLink="/contact"
        backgroundStyle="gradient"
      />

      {/* Our Services Overview */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              Our Services
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-6 text-gray-900">
              Digital Solutions for Modern Businesses
            </h2>
            <p className="md:text-lg text-gray-600">
              We help businesses transform ideas into scalable, high-performance digital solutions. Our team specializes in crafting user-centric web platforms, mobile apps, data solutions, and design systems using modern technologies and agile workflows.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition duration-300 border-t-4 border-blue-600">
              <div className="w-14 h-14 mb-6 rounded-lg flex items-center justify-center bg-blue-50 text-blue-600">
                <Globe size={28} />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-800">Web Development</h3>
              <p className="text-gray-600 mb-6">
                Robust, responsive web applications and sites that deliver exceptional user experiences and drive business growth.
              </p>
              <Link
                to="#web-development"
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
              >
                Learn more
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition duration-300 border-t-4 border-indigo-600">
              <div className="w-14 h-14 mb-6 rounded-lg flex items-center justify-center bg-indigo-50 text-indigo-600">
                <Smartphone size={28} />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-800">Mobile App Development</h3>
              <p className="text-gray-600 mb-6">
                Native and cross-platform mobile applications that engage users and provide seamless experiences on iOS and Android.
              </p>
              <Link
                to="#mobile-development"
                className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Learn more
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition duration-300 border-t-4 border-purple-600">
              <div className="w-14 h-14 mb-6 rounded-lg flex items-center justify-center bg-purple-50 text-purple-600">
                <PenTool size={28} />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-800">UI/UX Design</h3>
              <p className="text-gray-600 mb-6">
                Intuitive, accessible, and visually striking interfaces that engage users and support your brand identity.
              </p>
              <Link
                to="#ui-ux-design"
                className="inline-flex items-center text-purple-600 hover:text-purple-700 font-medium"
              >
                Learn more
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition duration-300 border-t-4 border-green-600">
              <div className="w-14 h-14 mb-6 rounded-lg flex items-center justify-center bg-green-50 text-green-600">
                <BarChart3 size={28} />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-800">Data Analytics</h3>
              <p className="text-gray-600 mb-6">
                Transform raw data into strategic insights through visualization, dashboards, and AI-powered analytics tools.
              </p>
              <Link
                to="#data-analytics"
                className="inline-flex items-center text-green-600 hover:text-green-700 font-medium"
              >
                Learn more
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Web Development */}
      <section id="web-development" className="py-16 md:py-24 bg-gray-50 scroll-mt-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="lg:w-1/2">
              <div className="relative">
                <div className="rounded-xl overflow-hidden shadow-xl">
                  <div className="aspect-video bg-gradient-to-r from-blue-500 to-blue-700 flex items-center justify-center">
                    <div className="relative w-3/4 h-3/4 overflow-hidden">
                      {/* Web code snippet */}
                      <pre className="text-white text-opacity-30 text-xs md:text-sm overflow-hidden font-mono">
{`import React, { useState, useEffect } from 'react';
import axios from 'axios';

function App() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get('/api/data');
        setData(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="container">
      <header>
        <h1>Web Application</h1>
      </header>
      <main>
        {loading ? (
          <p>Loading data...</p>
        ) : (
          <ul>
            {data.map(item => (
              <li key={item.id}>{item.name}</li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}`}
                      </pre>

                      {/* Browser frame overlay */}
                      <div className="absolute top-0 left-0 right-0 h-6 bg-gray-800 flex items-center px-2">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                          <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        </div>
                        <div className="mx-auto text-xs text-white text-opacity-50">localhost:3000</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-blue-100 rounded-full opacity-50"></div>
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-indigo-100 rounded-full opacity-50"></div>
              </div>
            </div>

            <div className="lg:w-1/2 px-4">
              <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
                Web Development
              </span>
              <h2 className="text-2xl md:text-4xl font-bold mb-6 text-gray-900">
                Robust, Responsive Web Solutions
              </h2>
              <p className="text-gray-600 mb-6">
                We build robust, responsive, and secure websites tailored to your business goals—whether it's a sleek marketing site or a complex web application.
              </p>

              <div className="mb-8">
                <h3 className="text-xl font-bold mb-4 text-gray-800">Technologies We Use:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-blue-500" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-800">Frontend</h4>
                      <p className="text-gray-600">React.js, Vue.js, Next.js, HTML5, CSS3, Tailwind CSS</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-blue-500" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-800">Backend</h4>
                      <p className="text-gray-600">Node.js, Django, Flask, Express.js</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-blue-500" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-800">Databases</h4>
                      <p className="text-gray-600">PostgreSQL, MySQL, MongoDB</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-blue-500" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-800">CMS</h4>
                      <p className="text-gray-600">WordPress, Strapi, Sanity</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">Our Deliverables:</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <Code className="h-5 w-5 text-blue-500" />
                    <span className="text-gray-700">Custom Web Applications</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Server className="h-5 w-5 text-blue-500" />
                    <span className="text-gray-700">Scalable Backend Systems</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Database className="h-5 w-5 text-blue-500" />
                    <span className="text-gray-700">RESTful & GraphQL APIs</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <ShoppingCart className="h-5 w-5 text-blue-500" />
                    <span className="text-gray-700">E-commerce Platforms</span>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition duration-300 shadow-md"
                >
                  Discuss Your Web Project
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Development */}
      <section id="mobile-development" className="py-16 md:py-24 bg-white scroll-mt-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row-reverse gap-12 items-center">
            <div className="w-full md:w-1/2 lg:w-1/2">
              <div className="relative">
                <div className="rounded-xl overflow-hidden shadow-xl">
                  <div className="aspect-video bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center">
                    <div className="relative h-4/5 flex items-center justify-center">
                      {/* Phone mockup */}
                      <div className="relative w-44 h-full bg-gray-800 rounded-3xl p-2 border-4 border-gray-700">
                        {/* Screen */}
                        <div className="w-full h-full bg-gray-100 rounded-2xl overflow-hidden">
                          {/* App screen mockup */}
                          <div className="h-full flex flex-col">
                            {/* Status bar */}
                            <div className="h-6 bg-indigo-600 flex items-center justify-between px-3">
                              <div className="text-white text-opacity-80 text-xs">9:41</div>
                              <div className="flex space-x-1">
                                <div className="w-2 h-2 rounded-full bg-white bg-opacity-80"></div>
                                <div className="w-2 h-2 rounded-full bg-white bg-opacity-80"></div>
                                <div className="w-2 h-2 rounded-full bg-white bg-opacity-80"></div>
                              </div>
                            </div>

                            {/* App content */}
                            <div className="flex-grow bg-white p-2 flex flex-col">
                              <div className="h-8 bg-indigo-100 rounded-lg mb-2 flex items-center justify-center">
                                <span className="text-indigo-600 text-xs font-semibold">App Title</span>
                              </div>
                              <div className="flex-grow space-y-2">
                                <div className="h-16 bg-gray-100 rounded-lg flex p-2">
                                  <div className="w-12 h-12 bg-indigo-200 rounded-lg"></div>
                                  <div className="ml-2 flex-grow">
                                    <div className="h-3 w-20 bg-gray-300 rounded-full mb-1"></div>
                                    <div className="h-2 w-full bg-gray-200 rounded-full mb-1"></div>
                                    <div className="h-2 w-16 bg-gray-200 rounded-full"></div>
                                  </div>
                                </div>
                                <div className="h-16 bg-gray-100 rounded-lg flex p-2">
                                  <div className="w-12 h-12 bg-purple-200 rounded-lg"></div>
                                  <div className="ml-2 flex-grow">
                                    <div className="h-3 w-24 bg-gray-300 rounded-full mb-1"></div>
                                    <div className="h-2 w-full bg-gray-200 rounded-full mb-1"></div>
                                    <div className="h-2 w-20 bg-gray-200 rounded-full"></div>
                                  </div>
                                </div>
                                <div className="h-16 bg-gray-100 rounded-lg flex p-2">
                                  <div className="w-12 h-12 bg-blue-200 rounded-lg"></div>
                                  <div className="ml-2 flex-grow">
                                    <div className="h-3 w-16 bg-gray-300 rounded-full mb-1"></div>
                                    <div className="h-2 w-full bg-gray-200 rounded-full mb-1"></div>
                                    <div className="h-2 w-12 bg-gray-200 rounded-full"></div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Navigation bar */}
                            <div className="h-12 bg-white border-t border-gray-200 flex items-center justify-around px-2">
                              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                <div className="w-4 h-4 bg-indigo-500 rounded-sm"></div>
                              </div>
                              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                <div className="w-4 h-4 bg-gray-400 rounded-full"></div>
                              </div>
                              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                <div className="w-4 h-4 bg-gray-400 rounded-sm"></div>
                              </div>
                              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                <div className="w-4 h-4 bg-gray-400 rounded-sm"></div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Home button */}
                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gray-600 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-indigo-100 rounded-full opacity-50"></div>
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-purple-100 rounded-full opacity-50"></div>
              </div>
            </div>

            <div className="px-4 w-full md:w-1/2 lg:w-1/2">
              <span className="inline-block px-3 py-1 rounded-full bg-indigo-100 text-indigo-600 text-sm font-medium mb-4">
                Mobile App Development
              </span>
              <h2 className="text-2xl md:text-4xl font-bold mb-6 text-gray-900">
                Exceptional Mobile Experiences
              </h2>
              <p className="text-gray-600 mb-6">
                From MVPs to full-scale mobile products, we create performant apps that deliver exceptional user experiences on Android and iOS platforms.
              </p>

              <div className="mb-8">
                <h3 className="text-xl font-bold mb-4 text-gray-800">Technologies We Use:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-indigo-500" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-800">Cross-platform</h4>
                      <p className="text-gray-600">React Native, Flutter, Expo</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-indigo-500" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-800">Native</h4>
                      <p className="text-gray-600">Swift (iOS), Kotlin (Android)</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-indigo-500" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-800">Backend</h4>
                      <p className="text-gray-600">Firebase, AWS Amplify, Django REST Framework</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-indigo-500" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-800">CI/CD</h4>
                      <p className="text-gray-600">Fastlane, Bitrise</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">Our Deliverables:</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <Smartphone className="h-5 w-5 text-indigo-500" />
                    <span className="text-gray-700">Hybrid & Native Apps</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Layers className="h-5 w-5 text-indigo-500" />
                    <span className="text-gray-700">Real-time Features</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Zap className="h-5 w-5 text-indigo-500" />
                    <span className="text-gray-700">App Store Deployment</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Cloud className="h-5 w-5 text-indigo-500" />
                    <span className="text-gray-700">Cloud Integration</span>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition duration-300 shadow-md"
                >
                  Discuss Your Mobile App
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UI/UX Design */}
      <section id="ui-ux-design" className="py-16 md:py-24 bg-gray-50 scroll-mt-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="w-full md:w-1/2 lg:w-1/2">
              <div className="relative">
                <div className="rounded-xl overflow-hidden shadow-xl">
                  <div className="aspect-video bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                    <div className="relative w-4/5 h-4/5 flex flex-col">
                      {/* Design tool mockup */}
                      <div className="flex items-center bg-gray-900 h-8 rounded-t-lg px-3 space-x-2">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                          <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        </div>
                        <div className="text-gray-400 text-xs ml-2">Design Tool</div>
                      </div>

                      <div className="flex-grow bg-gray-100 rounded-b-lg flex">
                        {/* Left sidebar */}
                        <div className="w-12 bg-gray-800 flex flex-col items-center py-3 space-y-4">
                          <div className="w-6 h-6 rounded-md bg-purple-400"></div>
                          <div className="w-6 h-6 rounded-md bg-gray-600"></div>
                          <div className="w-6 h-6 rounded-md bg-gray-600"></div>
                          <div className="w-6 h-6 rounded-md bg-gray-600"></div>
                        </div>

                        {/* Main content */}
                        <div className="flex-grow p-3 bg-gray-200">
                          {/* Design frames */}
                          <div className="flex space-x-3">
                            <div className="w-1/3 h-48 bg-white rounded-lg shadow-sm flex flex-col p-2">
                              <div className="h-6 bg-purple-100 mb-2 rounded"></div>
                              <div className="h-24 bg-purple-50 mb-2 rounded flex items-center justify-center">
                                <div className="w-16 h-16 bg-purple-200 rounded-lg"></div>
                              </div>
                              <div className="h-4 bg-gray-100 mb-1 rounded"></div>
                              <div className="h-4 bg-gray-100 rounded"></div>
                            </div>

                            <div className="w-1/3 h-48 bg-white rounded-lg shadow-sm flex flex-col p-2">
                              <div className="h-6 bg-purple-100 mb-2 rounded"></div>
                              <div className="h-8 bg-purple-50 mb-2 rounded"></div>
                              <div className="h-8 bg-purple-50 mb-2 rounded"></div>
                              <div className="h-8 bg-purple-50 mb-2 rounded"></div>
                              <div className="h-8 bg-purple-200 rounded"></div>
                            </div>

                            <div className="w-1/3 h-48 bg-white rounded-lg shadow-sm flex flex-col p-2">
                              <div className="h-6 bg-purple-100 mb-2 rounded"></div>
                              <div className="flex-grow grid grid-cols-2 gap-2">
                                <div className="bg-purple-50 rounded"></div>
                                <div className="bg-purple-50 rounded"></div>
                                <div className="bg-purple-50 rounded"></div>
                                <div className="bg-purple-200 rounded"></div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Right sidebar */}
                        <div className="w-32 bg-gray-800 p-3 flex flex-col space-y-3">
                          <div className="h-4 bg-gray-600 rounded"></div>
                          <div className="h-4 bg-gray-600 rounded"></div>
                          <div className="h-24 bg-gray-700 rounded"></div>
                          <div className="h-12 bg-gray-700 rounded"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-purple-100 rounded-full opacity-50"></div>
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-pink-100 rounded-full opacity-50"></div>
              </div>
            </div>

            <div className="lg:w-1/2 px-4">
              <span className="inline-block px-3 py-1 rounded-full bg-purple-100 text-purple-600 text-sm font-medium mb-4">
                UI/UX Design
              </span>
              <h2 className="text-2xl md:text-4xl font-bold mb-6 text-gray-900">
                Intuitive, Engaging Interfaces
              </h2>
              <p className="text-gray-600 mb-6">
                We design intuitive, accessible, and visually striking interfaces that engage users and support your brand identity.
              </p>

              <div className="mb-8">
                <h3 className="text-xl font-bold mb-4 text-gray-800">Tools & Frameworks:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-purple-500" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-800">Design</h4>
                      <p className="text-gray-600">Figma, Adobe XD, Sketch</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-purple-500" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-800">Prototyping</h4>
                      <p className="text-gray-600">InVision, Framer</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-purple-500" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-800">Design Systems</h4>
                      <p className="text-gray-600">Tailwind UI, Material Design, Ant Design</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">Our Focus Areas:</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <Figma className="h-5 w-5 text-purple-500" />
                    <span className="text-gray-700">Wireframes & Prototypes</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Layout className="h-5 w-5 text-purple-500" />
                    <span className="text-gray-700">High-Fidelity UI Design</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Users className="h-5 w-5 text-purple-500" />
                    <span className="text-gray-700">UX Research & Testing</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Layers className="h-5 w-5 text-purple-500" />
                    <span className="text-gray-700">Design System Creation</span>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 rounded-lg bg-purple-600 text-white font-medium hover:bg-purple-700 transition duration-300 shadow-md"
                >
                  Discuss Your Design Project
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Data Analytics */}
      <section id="data-analytics" className="py-16 md:py-24 bg-white scroll-mt-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row-reverse gap-12 items-center">
            <div className="lg:w-1/2">
              <div className="relative">
                <div className="rounded-xl overflow-hidden shadow-xl">
                  <div className="aspect-video bg-gradient-to-r from-green-500 to-teal-500 flex items-center justify-center">
                    <div className="relative w-4/5 h-4/5 bg-white rounded-lg shadow-lg overflow-hidden flex flex-col">
                      {/* Dashboard header */}
                      <div className="h-12 bg-green-600 flex items-center justify-between px-4">
                        <div className="text-white font-semibold">Analytics Dashboard</div>
                        <div className="flex space-x-2">
                          <div className="w-6 h-6 bg-green-500 rounded flex items-center justify-center">
                            <div className="w-3 h-3 bg-white rounded-sm"></div>
                          </div>
                          <div className="w-6 h-6 bg-green-500 rounded flex items-center justify-center">
                            <div className="w-3 h-3 bg-white rounded-sm"></div>
                          </div>
                        </div>
                      </div>

                      {/* Dashboard content */}
                      <div className="flex-grow p-4 bg-gray-50 grid grid-cols-2 gap-4">
                        {/* Charts */}
                        <div className="bg-white rounded-lg shadow-sm p-3 flex flex-col">
                          <div className="text-xs font-semibold text-gray-700 mb-2">Revenue Overview</div>
                          <div className="flex-grow flex items-end space-x-1">
                            <div className="w-1/6 bg-green-200 rounded-t" style={{height: '30%'}}></div>
                            <div className="w-1/6 bg-green-300 rounded-t" style={{height: '50%'}}></div>
                            <div className="w-1/6 bg-green-400 rounded-t" style={{height: '40%'}}></div>
                            <div className="w-1/6 bg-green-500 rounded-t" style={{height: '70%'}}></div>
                            <div className="w-1/6 bg-green-600 rounded-t" style={{height: '60%'}}></div>
                            <div className="w-1/6 bg-green-700 rounded-t" style={{height: '80%'}}></div>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-sm p-3 flex flex-col">
                          <div className="text-xs font-semibold text-gray-700 mb-2">User Activity</div>
                          <div className="flex-grow flex items-center">
                            <div className="w-32 h-32 rounded-full border-8 border-green-100 relative">
                              <div className="absolute inset-0 border-8 border-green-500 rounded-full" style={{clipPath: 'polygon(0 0, 100% 0, 100% 70%, 0% 70%)'}}></div>
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-lg font-bold text-green-600">70%</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-sm p-3 col-span-2">
                          <div className="text-xs font-semibold text-gray-700 mb-2">Performance Metrics</div>
                          <div className="grid grid-cols-4 gap-2">
                            <div className="bg-gray-50 rounded p-2 text-center">
                              <div className="text-xs text-gray-500">Sales</div>
                              <div className="text-green-600 font-semibold">$24.5k</div>
                            </div>
                            <div className="bg-gray-50 rounded p-2 text-center">
                              <div className="text-xs text-gray-500">Orders</div>
                              <div className="text-green-600 font-semibold">432</div>
                            </div>
                            <div className="bg-gray-50 rounded p-2 text-center">
                              <div className="text-xs text-gray-500">Customers</div>
                              <div className="text-green-600 font-semibold">1.2k</div>
                            </div>
                            <div className="bg-gray-50 rounded p-2 text-center">
                              <div className="text-xs text-gray-500">Growth</div>
                              <div className="text-green-600 font-semibold">+12%</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-green-100 rounded-full opacity-50"></div>
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-teal-100 rounded-full opacity-50"></div>
              </div>
            </div>

            <div className="lg:w-1/2 px-4">
              <span className="inline-block px-3 py-1 rounded-full bg-green-100 text-green-600 text-sm font-medium mb-4">
                Data Analytics
              </span>
              <h2 className="text-2xl md:text-4xl font-bold mb-6 text-gray-900">
                Data-Driven Insights
              </h2>
              <p className="text-gray-600 mb-6">
                Make informed decisions with our data analytics services—transforming raw data into strategic insights through visualization, dashboards, and AI-powered tools.
              </p>

              <div className="mb-8">
                <h3 className="text-xl font-bold mb-4 text-gray-800">Technologies We Use:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-green-500" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-800">BI Tools</h4>
                      <p className="text-gray-600">Power BI, Tableau, Google Data Studio</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-green-500" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-800">Programming</h4>
                      <p className="text-gray-600">Python (Pandas, NumPy), R</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-green-500" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-800">Databases</h4>
                      <p className="text-gray-600">BigQuery, Redshift, PostgreSQL</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Check className="h-5 w-5 text-green-500" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-800">ML/AI</h4>
                      <p className="text-gray-600">Scikit-learn, TensorFlow, OpenAI APIs</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">Our Offerings:</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <Database className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">Data Cleaning & Processing</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <PieChart className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">Interactive Dashboards</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <FileText className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">Business Intelligence</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Brain className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">Predictive Analytics & AI</span>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 transition duration-300 shadow-md"
                >
                  Discuss Your Data Needs
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>


      <CtaSection
        title="Ready to build something amazing?"
        subtitle="Let's discuss how our services can help you achieve your business goals. Contact us today to get started."
        buttonText="Start Your Project"
        buttonLink="/contact"
        sectionStyle="gradient"
      />
    </>
  );
};

export default ServicesPage;
